import React from 'react';
import _ from 'underscore';

import { CommonStyles } from '../styles/CommonStyles';
import { imgUrl } from '../utils/ImgUrl';

export interface Hoverable {
  hovered?: boolean;
  suppressHover?: boolean;
}

export interface IconProps extends Hoverable {
  disabled?: boolean;
  testId?: string;

  // These HTML props are manipulated by the Icon component before being passed through
  style?: React.CSSProperties;
  hoverStyle?: React.CSSProperties;
  onMouseEnter?: React.MouseEventHandler<HTMLSpanElement>;
  onMouseLeave?: React.MouseEventHandler<HTMLSpanElement>;

  // These HTML props are passed through to the rendered element
  title?: string;
}

export type IconSize = 'normal' | 'small' | 'large';

export interface IconDefinition {
  name: string;
  img: string;
  hoverImg?: string;
  disabledImg?: string;
  size: IconSize;
  style?: React.CSSProperties;
}

const IconSizes: _.Dictionary<number> = {
  small: CommonStyles.Sizes.SmallIconSize,
  normal: CommonStyles.Sizes.NormalIconSize,
  large: CommonStyles.Sizes.LargeIconSize
};

export function iconStyle(iconDefinition: IconDefinition): React.CSSProperties {
  const iconSize = IconSizes[iconDefinition.size];

  return _.extend({}, CommonStyles.Box, {
    backgroundSize: iconSize + 'px ' + iconSize + 'px',
    backgroundPosition: 'center center',
    backgroundRepeat: 'no-repeat',
    width: iconSize,
    height: iconSize,
  });
}

export function icon(iconDefinition: IconDefinition): React.ComponentClass<IconProps> {

  const getBackgroundImageUrl = () => imgUrl(iconDefinition.img);
  const getBackgroundImageUrlHovered = () => (iconDefinition.hoverImg ? imgUrl(iconDefinition.hoverImg) : getBackgroundImageUrl());
  const getBackgroundImageUrlDisabled = () => (iconDefinition.disabledImg ? imgUrl(iconDefinition.disabledImg) : getBackgroundImageUrl());

  return class extends React.PureComponent<IconProps, Hoverable> {

    public static displayName = iconDefinition.name;

    constructor(props: IconProps) {
      super(props);
      this.state = { hovered: false };
    }

    public render() {

      return <span title={ this.props.title }
                   style={ this.style() }
                   onMouseEnter={ this.onMouseEnter }
                   onMouseLeave={ this.onMouseLeave }
                   data-tb-test-id={ this.props.testId } />;
    }

    private onMouseEnter = (event: React.MouseEvent<HTMLSpanElement>) => {
      this.setHovered(true);
      if (this.props.onMouseEnter) this.props.onMouseEnter(event);
    };

    private onMouseLeave = (event: React.MouseEvent<HTMLSpanElement>) => {
      this.setHovered(false);
      if (this.props.onMouseLeave) this.props.onMouseLeave(event);
    };

    private setHovered(hovered: boolean) {
      this.setState({ hovered });
    }

    private isHovered(): boolean {
      return this.props.suppressHover ? false : (this.state.hovered || this.props.hovered);
    }

    private style() {

      let style = {
        ...iconStyle(iconDefinition),
        backgroundImage: this.isHovered() ? getBackgroundImageUrlHovered() : this.props.disabled ? getBackgroundImageUrlDisabled() : getBackgroundImageUrl(),
        ...iconDefinition.style,
        ...this.props.style
      };

      const hoverStyle = this.props.hoverStyle;
      if (this.isHovered() && hoverStyle) {
        style = { ...style, ...hoverStyle };
      }

      return style;
    }
  };
}

export const Icons = {
  actionIcon: icon({
    name: 'actionIcon',
    img: 'actions_normal.svg',
    hoverImg: 'actions_hover.svg',
    disabledImg: 'actions_disabled.svg',
    size: 'normal'
  }),

  dataConnectionFile: icon({
    name: 'dataConnectionIconFile',
    img: 'file_connection_type_small.svg',
    size: 'small'
  }),

  dataConnectionExternal: icon({
    name: 'dataConnectionIconExternal',
    img: 'external_server_connection_type_small.svg',
    size: 'small'
  }),

  dataConnectionTableauServer: icon({
    name: 'dataConnectionIconTableauServer',
    img: 'tableau_server_connection_type_small.svg',
    size: 'small'
  }),

  dataConnectionVirtualConnection: icon({
    name: 'dataConnectionIconVirtualConnection',
    img: 'datasource_server_connection_base_14px.svg',
    size: 'small'
  }),

  sharedViews: icon({
    name: 'sharedViews',
    img: 'view_pencil_medium.svg',
    hoverImg: 'view_pencil_medium_active.svg',
    size: 'normal',
  })
};
